import { Link } from "react-router-dom";

/**
 * SwingTrust brand — Logo & ShieldMark.
 *
 * Two-mode design so the same component works pre- and post-asset arrival:
 *
 *   1. IMAGE MODE (post asset delivery — preferred). Set the constants
 *      LOCKUP_LIGHT_SRC / LOCKUP_DARK_SRC / SHIELD_SRC below to point at
 *      files under /app/frontend/public/brand/. As soon as they resolve to
 *      real files the component renders <img>, no other change needed.
 *
 *   2. TEXT MODE (fallback — current state). Renders the inline SVG shield
 *      + text wordmark exactly as before, so nothing breaks until real
 *      artwork is dropped in.
 *
 * Dark-background surfaces (AdminLayout sidebar + mobile header, email
 * template header) must render <Logo variant="dark" />. Light surfaces
 * default to variant="light".
 *
 * Alt text is fixed by design:
 *   full lockup  → "SwingTrust — Swing Trading Education Academy"
 *   shield only  → "SwingTrust"
 */

// Set to a truthy path (e.g. "/brand/swingtrust-lockup-light.svg") ONCE the
// user drops the real artwork into /app/frontend/public/brand/. Until then
// the component stays on text mode.
const LOCKUP_LIGHT_SRC = "/brand/swingtrust-lockup-light.png";
const LOCKUP_DARK_SRC = "/brand/swingtrust-lockup-dark.png";
// Standalone shield: user supplied ONLY the light-bg (blue) shield for MVP.
// Dark-surface shield placement falls back to the dark lockup (which already
// carries the shield glyph). Do NOT set SHIELD_DARK_SRC to the /tmp/dark
// shield — verified all-dark, invisible on navy backgrounds.
const SHIELD_SRC = "/brand/swingtrust-shield-light.png";

const LOCKUP_ALT = "SwingTrust — Swing Trading Education Academy";
const SHIELD_ALT = "SwingTrust";

// -- Shield mark (used inline in trust strips, non-brand contexts) -----------
export function ShieldMark({ size = 40 }) {
    if (SHIELD_SRC) {
        return (
            <img
                src={SHIELD_SRC}
                alt={SHIELD_ALT}
                width={size}
                height={size}
                style={{ display: "inline-block" }}
                data-testid="brand-shield-mark"
            />
        );
    }
    // Fallback: inline SVG with a stylized "S" swoosh (crisp at any size).
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            data-testid="brand-shield-mark"
        >
            <path
                d="M24 3.5L41 9.2v13.6c0 10.5-7.3 19.8-17 22.3-9.7-2.5-17-11.8-17-22.3V9.2L24 3.5z"
                fill="#0A1F44"
            />
            <path
                d="M24 3.5L41 9.2v13.6c0 10.5-7.3 19.8-17 22.3-9.7-2.5-17-11.8-17-22.3V9.2L24 3.5z"
                stroke="#1E48D6"
                strokeWidth="0.8"
            />
            <path
                d="M30.5 17.5c-1.5-1.6-4-2.6-6.6-2.6-3.7 0-6.4 1.9-6.4 4.7 0 2.6 2.2 3.9 5.7 4.7l1.6.4c2.4.6 3.3 1.2 3.3 2.4 0 1.4-1.5 2.4-3.7 2.4-2.4 0-4.4-1-5.9-2.7l-1.9 2.5c1.8 2 4.5 3.1 7.7 3.1 4 0 6.7-2 6.7-5 0-2.6-1.9-4-5.4-4.8l-1.7-.4c-2.7-.7-3.5-1.3-3.5-2.4 0-1.3 1.4-2.1 3.5-2.1 1.9 0 3.6.7 4.9 2l1.7-2.2z"
                fill="#1E48D6"
            />
        </svg>
    );
}

// -- Full lockup (default header/footer/email use) --------------------------
export default function Logo({ compact = false, linkTo = "/", variant = "light" }) {
    const src = variant === "dark" ? LOCKUP_DARK_SRC : LOCKUP_LIGHT_SRC;

    // Image mode — dropped in once user's SVG/PNG lockup is present.
    if (src) {
        const height = compact ? 32 : 44;   // ~40-48px desktop natural height
        const img = (
            <img
                src={src}
                alt={LOCKUP_ALT}
                style={{
                    height,
                    width: "auto",           // preserve aspect ratio, never stretch
                    maxHeight: height,
                    display: "block",
                }}
                data-testid="brand-logo"
            />
        );
        return linkTo ? <Link to={linkTo} className="inline-flex">{img}</Link> : img;
    }

    // Text-mode fallback — matches current on-screen brand exactly, and
    // adjusts wordmark colours for dark surfaces so the mobile admin header
    // (navy background) is legible even without the image asset.
    const swingColor = variant === "dark" ? "var(--st-white)" : "var(--st-navy)";
    const trustColor = "var(--st-royal)";
    const subtitleColor = variant === "dark" ? "#9AA6C4" : "var(--st-silver-500)";

    const content = (
        <div className="flex items-center gap-3" data-testid="brand-logo">
            <ShieldMark size={compact ? 32 : 40} />
            <div className="leading-none">
                <div className={`font-serif ${compact ? "text-xl" : "text-2xl"} font-semibold`}>
                    <span style={{ color: swingColor }}>Swing</span>
                    <span style={{ color: trustColor }}>Trust</span>
                </div>
                {!compact && (
                    <div
                        className="tracking-caps mt-1 text-[10px] font-semibold"
                        style={{ color: subtitleColor }}
                    >
                        Swing Trading Education Academy
                    </div>
                )}
            </div>
        </div>
    );
    if (linkTo) {
        return <Link to={linkTo} className="inline-flex">{content}</Link>;
    }
    return content;
}
